import 'reactflow/dist/style.css';
import AssetNode from 'components/Question/QuestionAssets/AssetNode';
import LinkToLRConnect from 'components/Question/QuestionAssets/LinkToLRConnect';
import LiveRampLoader from 'BaseComponents/LiveRampLoader';
import React, { useCallback, useEffect } from 'react';
import ReactFlow, {
  Background,
  Controls,
  ReactFlowProvider,
  addEdge,
  useEdgesState,
  useNodesState,
} from 'reactflow';
import {
  Alert, Stack, Typography,
} from '@mui/material';
import { MotifSidePanel } from 'BaseComponents/MotifSidePanel/MotifSidePanel';
import { MotifSidePanelContent } from 'BaseComponents/MotifSidePanel/MotifSidePanelContent';
import { MotifSidePanelHeader } from 'BaseComponents/MotifSidePanel/MotifSidePanelHeader';
import { useGetAssetAssociationGraph } from 'api/questions';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';


const nodeTypes = {
  assetNode: AssetNode,
};

const NODE_VERTICAL_SPACING = 150;
const NODE_HORIZONTAL_SPACING = 500;

const processGraphData = (graphData) => {
  if (!graphData || !graphData.graph) {
    return null;
  }

  const { asset, downstream } = graphData.graph;

  // Flatten the asset object
  const flattenedAsset = {
    ...asset.generic.identifier,
    ...asset.generic.cleanRoomDetails,
    ...asset.generic.catalogDetails,
  };

  // Flatten the downstream array
  const flattenedDownstream = downstream.map((downstreamItem) => ({
    ...downstreamItem.generic.identifier,
    ...downstreamItem.generic.cleanRoomDetails,
    ...downstreamItem.generic.catalogDetails,
  }));

  return {
    asset: flattenedAsset,
    downstream: flattenedDownstream,
  };
};

const processNodeData = (graphData) => {
  if (!graphData || !graphData.asset || !graphData.downstream) {
    return { nodes: [], edges: [] };
  }

  const { asset, downstream } = graphData;

  // Create the root node
  const nodes = [
    {
      id: asset.ID,
      type: 'assetNode',
      data: {
        id: asset.ID,
        name: asset.displayName,
        description: asset.description,
        assetType: asset.assetType,
      },
      position: { x: 0, y: 0 }, // Root node position
    },
  ];

  const edges = [];

  // Calculate the vertical offset to center the root node
  const totalDownstream = downstream.length;
  const rootYOffset = (totalDownstream - 1) * NODE_VERTICAL_SPACING / 2;

  // Map downstream nodes
  downstream.forEach((child, index) => {
    // Add downstream nodes
    nodes.push({
      id: child.ID,
      type: 'assetNode',
      data: {
        id: child.ID,
        name: child.displayName,
        description: child.description,
        assetType: child.assetType,
        isChildNode: true,
      },
      position: {
        x: NODE_HORIZONTAL_SPACING, // Horizontal spacing
        y: index * NODE_VERTICAL_SPACING - rootYOffset, // Centered vertically
      },
    });

    // Add edges for downstream.associations
    child.associations
      .filter((assoc) => assoc.type === 'CREATED_FROM')
      .forEach((assoc) => {
        edges.push({
          id: `edge-${assoc.id}-${child.ID}`,
          source: assoc.id,
          target: child.ID,
          type: 'default',
          animated: true,
        });
      });
  });

  return { nodes, edges };
};

const QuestionAssetsPanel = ({ questionId, onPanelClose }) => {
  const [graphNodes, setNodes, onNodesChange] = useNodesState();
  const [graphEdges, setEdges, onEdgesChange] = useEdgesState();

  const orgId = useSelector((state) => state.session.user.primaryOrganization.organization?.ID);
  const { crId } = useParams();

  const {
    mutateAsync: getAssetAssociationGraph,
    isError,
    isLoading,
  } = useGetAssetAssociationGraph(crId, questionId);

  useEffect(() => {
    const fetchGraphData = async () => {
      const response = await getAssetAssociationGraph({ depth: 10 });
      const graphData = processGraphData(response.data);

      if (graphData) {
        const { nodes: fetchedNodes, edges: fetchedEdges } = processNodeData(graphData);
        setNodes(fetchedNodes);
        setEdges(fetchedEdges);
      }
    };

    fetchGraphData();
  }, [getAssetAssociationGraph, orgId, setEdges, setNodes]);

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge(params, eds)),
    [setEdges],
  );

  return (
    <MotifSidePanel open onClose={onPanelClose} width={720}>
      <MotifSidePanelHeader onClose={onPanelClose}>
        <Typography variant='h4' aria-label='Segmentation Tree Header'>
          Segmentation Tree
        </Typography>
        <LinkToLRConnect />
      </MotifSidePanelHeader>
      <MotifSidePanelContent sx={{ p: 0, flexGrow: 1 }}>
        {isLoading && (
          <Stack justifyContent='center' alignItems='center' sx={{ height: '100%' }}>
            <LiveRampLoader />
          </Stack>
        )}
        {!isLoading && isError && (
          <Stack justifyContent='center' alignItems='center'>
            <Stack justifyContent='center' alignItems='center'>
              <Alert severity='error' sx={{ flexGrow: 'unset' }}>
                Failed to load segmentation tree.
              </Alert>
            </Stack>
          </Stack>
        )}
        {!isLoading && !isError && (
          <Stack direction='row'>
            <ReactFlowProvider>
              <div style={{ flexGrow: 1, overflow: 'hidden', width: '100%' }}>
                <ReactFlow
                  nodes={graphNodes}
                  edges={graphEdges}
                  nodeTypes={nodeTypes}
                  edgesFocusable={false}
                  fitView
                  nodesDraggable
                  nodesConnectable={false}
                  nodesFocusable={false}
                  onNodesChange={onNodesChange}
                  onEdgesChange={onEdgesChange}
                  onConnect={onConnect}
                >
                  <Background />
                  <Controls showInteractive={false} />
                </ReactFlow>
              </div>
            </ReactFlowProvider>
          </Stack>
        )}
      </MotifSidePanelContent>
    </MotifSidePanel>
  );
};

export default QuestionAssetsPanel;
